<script setup lang="ts">

import { ref, watch, computed } from 'vue'
import { BuildingLibraryIcon, CalendarDaysIcon } from "@heroicons/vue/24/solid";
import { storeToRefs } from "pinia";
import { useClaimStore } from "@/store";
import adminAPI from "@/services/api";
import {
  Flight,
  ListCompetentCourtsForClaimResponse,
  RetrieveClaimWithRelationsResponse
} from "@upclaim/sdk/src/data-contracts";
import { PencilSquareIcon, ArrowTopRightOnSquareIcon } from '@heroicons/vue/24/outline';
import HearingSelection from "@/components/overview/hearings/HearingSelection.vue";
import BaseLoader from "@/components/ui/loaders/BaseLoader.vue";
import { format } from "date-fns";
import { fr } from 'date-fns/locale';
import Alert from '@/components/ui/alerts/Alert.vue';
import openTribunalDigitalForCurrentClaim from '@/utils/open-tribunal-digital-procedure';
import { CalendarSearchIcon } from 'lucide-vue-next';
import BaseButton from '@/components/ui/buttons/BaseButton.vue';
import { CalendarCheck2Icon } from 'lucide-vue-next';
import { ArrowUpRightIcon } from 'lucide-vue-next';

const props = defineProps<{
  claim: RetrieveClaimWithRelationsResponse;
}>();
const claim = computed(() => props.claim);

const emits = defineEmits(["update-claim"]);

watch(claim, async () => {
  if (claim.value.id) {
    await fetchCompetentCourts(claim.value.id);
    editCourt.value = false
  }
});

const editCourt = ref<boolean>(false)
const competentCourts = ref<ListCompetentCourtsForClaimResponse>()
const isLoading = ref<boolean>(false);
const hearing = null;


if (claim.value.id) {
  await fetchCompetentCourts(claim.value.id);
}

const canSeeEditButton = computed<boolean>(() => {
  return !editCourt.value &&
    Object.values({ ...competentCourts.value }).filter(n => n).length > 0 &&
    (claim.value.claimState.type === 'COURT.WAIT_FOR_JURIDCTION_CHOICE'
      || claim.value.claimState.type === 'COURT.PROCEDURE_CLASSIQUE_WAIT_FOR_TRIAL_DATE_CHOICE')
    &&
    competentCourts.value?.airline !== null
})

async function fetchCompetentCourts(claimId: string) {
  isLoading.value = true
  const competentCourtsForClaim = await adminAPI.listCompetentCourtsForClaim(claimId);
  // @ts-ignore
  delete competentCourtsForClaim.all;
  isLoading.value = false
  if (competentCourtsForClaim) {
    competentCourts.value = competentCourtsForClaim;
  }
}

const isESCP = computed(() =>
  claim.value.legalSpecification.effectiveFlight.airline.isEC261
  && Object.values({ ...competentCourts.value }).every(x => x === null)
);
</script>
<template>
  <div class="bg-white">
    <div class="px-6 pt-6 pb-3">
      <div :class="[claim?.procedure?.nextHearing ? 'shadow border-double border-slate-200' : '']"
        class="px-6 py-5 border-2 border-dashed rounded-md">
        <div class="flex items-center justify-between">
          <BaseLoader v-if="isLoading" />
          <div v-else>
            <div class="flex items-center">
              <BuildingLibraryIcon class="w-6 h-6 text-gray-500 shrink-0" />
              <div class="mt-0 ml-4">
                <div v-if="isESCP &&!claim.procedure?.juridiction" class="text-sm text-left text-gray-900">
                  <p class="font-medium leading-7">
                    Procédure européenne
                  </p>
                  <!-- <p class="pr-12 text-gray-700">
                    Compagnie européenne mais pas de tribunal Français compétent.
                  </p> -->
                  <p class="pr-12 leading-5 text-gray-700">
                    Pas de tribunal Français compétent, mais la compagnie est européenne.
                  </p>
                </div>
                <div v-else-if="competentCourts?.airline === null && !claim.procedure.juridiction" class="text-sm text-left text-gray-800 ">
                  Aucun tribunal trouvé pour cette compagnie
                  <!-- La compagnie n'est pas assignable en France -->
                </div>
                <div v-else class="text-sm text-left text-gray-900"
                  :class="claim.procedure?.juridiction?.name ? 'font-medium ' : ''">
                  {{
                    claim?.procedure?.juridiction?.name ? claim.procedure.juridiction.name : 'Aucune audience enregistrée'
                  }}
                </div>
                <div v-if="claim?.procedure?.juridiction?.schedule" class="flex text-sm text-left text-gray-600">
                  <span class="hidden mx-2" aria-hidden="true">&middot;</span>
                  <div class="mt-1 text-xs italic">Ouverture :
                    {{
                      claim.procedure.juridiction.schedule
                    }}
                  </div>
                </div>
                <!-- <div v-if="claim?.procedure?.nextHearing?.hearingDate"
                     class="py-2 text-sm leading-6 text-left text-indigo-800">
                  Audience le : 
                  <span class="font-semibold">{{ format(new Date(claim.procedure.nextHearing.hearingDate), 'dd MMMM yyyy', {locale: fr}) }}</span>
                </div> -->
              </div>
            </div>
          </div>
          <div v-if="!isLoading" class="flex flex-col mt-4 sm:ml-6 sm:mt-0 sm:flex-shrink-0">
            <button v-if="canSeeEditButton" v-permission="['UPCLAIM_ADMIN', 'LAWYER']" type="button"
              @click="editCourt = true"
              class="inline-flex items-center px-3 py-2 mb-1 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              <PencilSquareIcon class="w-5 h-5 mr-2 text-gray-500" />
              Modifier
            </button>

            <BaseButton
              v-if="claim.procedure?.identifier"
              :icon="(ArrowUpRightIcon as any)"
              text="Voir l'Affaire"
              @click="openTribunalDigitalForCurrentClaim"
            />
            <a v-else-if="claim.procedure?.juridiction" target="_blank"
              :href="`https://www.infogreffe.fr/fiche-greffe/${claim.procedure.juridiction.code}`"
              class="inline-flex items-center px-3 py-2 text-sm font-semibold text-gray-900 bg-white rounded-md shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              <CalendarDaysIcon class="w-5 h-5 mr-2 text-gray-500" />
              Calendrier
            </a>
          </div>
        </div>
        <div class="mt-6" v-if="claim.procedure?.nextHearing">
          <Alert type="info" :with-border="false"
            :title="`Prochaine audience prévue le ${format(new Date(claim.procedure.nextHearing.hearingDate), 'dd MMMM yyyy', { locale: fr })}`" />
        </div>
      </div>
    </div>
    <HearingSelection v-if="competentCourts && editCourt" :claim="claim" :jurisdictions="competentCourts"
      @update-claim="$emit('update-claim')" />
  </div>
</template>
